import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import styles from './osReportV2Document.module.css';

function dash(value, fallback = '—') {
  const s = value == null ? '' : String(value).trim();
  return s || fallback;
}

function hasText(value) {
  return value != null && String(value).trim() !== '';
}

const CONT_HINT = 'Continua na próxima página ↓';
const LAYOUT_SAFETY_PX = 6;
/** Altura reservada ao cabeçalho das páginas de continuação (título + meta + borda). */
const CONT_HEADER_PX = 60;
const MAX_CONT_PAGES = 12;
const MM_PX = 96 / 25.4;
/** Espelha .fotosGrid (grid-auto-rows 122mm, gap 6mm) + título da seção de fotos. */
const FOTO_ROW_PX = 122 * MM_PX;
const FOTO_GAP_PX = 6 * MM_PX;
const FOTO_TITLE_PX = 40;

function listFotos(d) {
  return Array.isArray(d.closure?.fotos) ? d.closure.fotos.filter((f) => f && f.src) : [];
}

function fotoRowsFit(space) {
  const rows = Math.floor((space - FOTO_TITLE_PX + FOTO_GAP_PX) / (FOTO_ROW_PX + FOTO_GAP_PX));
  return Math.max(0, Math.min(2, rows));
}

function splitTokens(text) {
  return String(text || '').split(/(\s+)/).filter(Boolean);
}

function fitCount(units, avail, measure) {
  if (!units.length || measure(units[0]) > avail) return 0;
  let lo = 1;
  let hi = units.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (measure(units.slice(0, mid).join('').trimEnd()) <= avail) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Mede a página 1 e, se o conteúdo estourar o A4, distribui o laudo técnico
 * (quebrando o texto de "Observações" por palavras) em páginas de continuação.
 */
function computeLaudoLayout({ page, card, condLabel, obsLabel, obsBox }, rawText, filled) {
  if (page.scrollHeight <= page.clientHeight + 1) return { fit: true };

  const ps = window.getComputedStyle(page);
  const cs = window.getComputedStyle(card);
  const bs = window.getComputedStyle(obsBox);
  const padTop = parseFloat(ps.paddingTop) || 0;
  const padBottom = parseFloat(ps.paddingBottom) || 0;

  const cardTop = card.offsetTop;
  const obsTop = obsBox.offsetTop;
  const obsBottom = obsTop + obsBox.offsetHeight;
  const headFull = obsTop - cardTop;
  const headNoCond = headFull - (obsLabel.offsetTop - condLabel.offsetTop);
  const headTitleOnly = headNoCond - (obsTop - obsLabel.offsetTop);
  const tail = cardTop + card.offsetHeight - obsBottom;
  const bottomMid =
    (parseFloat(bs.marginBottom) || 0) +
    (parseFloat(cs.paddingBottom) || 0) +
    (parseFloat(cs.borderBottomWidth) || 0);
  const spaceP1 = page.clientHeight - padBottom - cardTop - LAYOUT_SAFETY_PX;
  const contSpace = page.clientHeight - padTop - padBottom - CONT_HEADER_PX - LAYOUT_SAFETY_PX;
  const text = filled ? String(rawText || '').trim() : '';

  const measurer = obsBox.cloneNode(false);
  Object.assign(measurer.style, {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: '0',
    top: '0',
    margin: '0',
    width: `${obsBox.offsetWidth}px`,
  });
  const hintEl = document.createElement('span');
  hintEl.className = styles.contHint;
  hintEl.textContent = CONT_HINT;
  page.appendChild(measurer);
  const measure = (s, withHint) => {
    measurer.textContent = s;
    if (withHint) measurer.appendChild(hintEl);
    return measurer.offsetHeight;
  };
  const measureHint = (s) => measure(s, true);

  try {
    let p1 = null;
    let rest = text;
    if (filled && text) {
      const units = splitTokens(text);
      const n = fitCount(units, spaceP1 - headFull - bottomMid, measureHint);
      if (n > 0) {
        p1 = units.slice(0, n).join('').trimEnd();
        rest = units.slice(n).join('').trimStart();
      }
    }

    const movedAll = p1 == null;
    const pages = [];
    let first = true;
    let freeSpace = 0;
    while (pages.length < MAX_CONT_PAGES) {
      const showCond = movedAll && first;
      const showObs = (movedAll && first) || !!rest;
      if (!showObs) {
        if (headTitleOnly + tail <= contSpace) {
          pages.push({ showCond: false, showObs: false, text: '', last: true });
          freeSpace = contSpace - headTitleOnly - tail;
        }
        break;
      }
      const head = showCond ? headFull : headNoCond;
      const h = filled ? measure(rest, false) : obsBox.offsetHeight;
      if (!filled || head + h + tail <= contSpace) {
        pages.push({ showCond, showObs: true, text: rest, last: true });
        freeSpace = Math.max(0, contSpace - head - h - tail);
        break;
      }
      const avail = contSpace - head - bottomMid;
      let units = splitTokens(rest);
      let n = fitCount(units, avail, measureHint);
      if (n === 0) {
        units = Array.from(rest);
        n = Math.max(1, fitCount(units, avail, measureHint));
      }
      pages.push({ showCond, showObs: true, text: units.slice(0, n).join('').trimEnd(), last: false });
      rest = units.slice(n).join('').trimStart();
      first = false;
    }
    return { fit: false, p1, pages, freeSpace };
  } finally {
    measurer.remove();
  }
}

function toCursiveName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/(^|\s)(\S)/g, (_, sp, ch) => sp + ch.toUpperCase());
}

function OsReportV2MainPage({ d }) {
  const c = d.closure && typeof d.closure === 'object' ? d.closure : null;
  const companySub = [d.empCnpj ? `CNPJ: ${d.empCnpj}` : null, d.empEmail || null]
    .filter(Boolean)
    .join(' • ');
  const logoUrl = String(d.companyLogoUrl || '').trim();
  const serie = String(d.numeroSerie || '').trim();
  const patrimonio = String(d.patrimonio || '').trim();
  const serieDisplay = serie || (!patrimonio ? dash(d.identificacao) : '—');

  const pageRef = useRef(null);
  const laudoRefs = {
    card: useRef(null),
    condLabel: useRef(null),
    obsLabel: useRef(null),
    obsBox: useRef(null),
  };
  const [layout, setLayout] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    let alive = true;
    document.fonts?.ready?.then(() => {
      if (alive) setFontsReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const measureKey = [
    d.empresaNome,
    d.empEndereco,
    d.cliente,
    d.endereco,
    d.localizacao,
    d.equipamento,
    d.defeito,
    d.observacao,
    d.observacaoGeral,
    d.abertura,
    c?.condicao,
    c?.laudo,
    c?.tecnico,
    c?.solicitanteNome,
    c?.assinaturaSrc ? 1 : 0,
    c ? 1 : 0,
    fontsReady ? 1 : 0,
  ].join('\u0001');

  useLayoutEffect(() => {
    setLayout(null);
  }, [measureKey]);

  useLayoutEffect(() => {
    if (layout) return;
    const els = {
      page: pageRef.current,
      card: laudoRefs.card.current,
      condLabel: laudoRefs.condLabel.current,
      obsLabel: laudoRefs.obsLabel.current,
      obsBox: laudoRefs.obsBox.current,
    };
    if (Object.values(els).some((el) => !el)) return;
    setLayout(computeLaudoLayout(els, c?.laudo, !!c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const fotos = listFotos(d);
  const lastPart = layout && !layout.fit ? layout.pages[layout.pages.length - 1] : null;
  const inlineFotos = lastPart?.last ? Math.min(fotos.length, fotoRowsFit(layout.freeSpace) * 2) : 0;

  return (
    <>
    <section ref={pageRef} className={styles.page} data-os-report-page="1">
      <table className={styles.headerTable}>
        <tbody>
          <tr>
            <td>
              <div className={styles.companyBlock}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className={styles.companyLogo}
                  />
                ) : null}
                <div className={styles.companyTitle}>
                  <h1>{dash(d.empresaNome)}</h1>
                  <p>
                    {companySub || dash(d.empEndereco, '')}
                    {companySub && d.empEndereco ? (
                      <>
                        <br />
                        {d.empEndereco}
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            </td>
            <td className={styles.osInfo}>
              <div className={styles.osNumber}>OS {dash(d.osNumber)}</div>
              <span className={styles.statusBadge}>{dash(d.tipoLabel || d.statusLabel, 'OS')}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div className={styles.sectionTitle}>Informações do Cliente</div>
      <div className={styles.card}>
        <table className={styles.formTable}>
          <tbody>
            <tr>
              <td style={{ width: '70%' }}>
                <span className={styles.fieldLabel}>Cliente / Razão Social</span>
                <span className={styles.fieldValue}>
                  {dash(d.cliente)}
                  {String(d.clienteDoc || '').trim() ? (
                    <>
                      <span className={styles.serieDot} aria-hidden>
                        ·
                      </span>
                      <span>{String(d.clienteDoc).trim()}</span>
                    </>
                  ) : null}
                </span>
              </td>
              <td style={{ width: '30%' }}>
                <span className={styles.fieldLabel}>Solicitante</span>
                <span className={styles.fieldValue}>{dash(d.solicitante)}</span>
              </td>
            </tr>
            <tr>
              <td style={{ paddingTop: 10 }}>
                <span className={styles.fieldLabel}>Endereço</span>
                <span className={styles.fieldValue}>{dash(d.endereco)}</span>
              </td>
              <td style={{ paddingTop: 10 }}>
                <span className={styles.fieldLabel}>Previsão</span>
                <span className={styles.fieldValue}>{dash(d.previsao, 'Sem previsão')}</span>
              </td>
            </tr>
            {hasText(d.localizacao) ? (
              <tr>
                <td colSpan={2} style={{ paddingTop: 10 }}>
                  <span className={styles.fieldLabel}>Localização</span>
                  <span className={styles.fieldValue}>{String(d.localizacao).trim()}</span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className={styles.sectionTitle}>Equipamento / Objeto</div>
      <div className={`${styles.card} ${styles.equipCard}`}>
        <table className={styles.formTable}>
          <tbody>
            <tr>
              <td style={{ width: '50%' }}>
                <span className={styles.fieldLabel}>Equipamento / Modelo</span>
                <span className={styles.fieldValue}>{dash(d.equipamento)}</span>
              </td>
              <td style={{ width: '50%' }}>
                <span className={styles.fieldLabel}>Nº de Série</span>
                <span className={styles.fieldValue}>
                  {serieDisplay}
                  {serie && patrimonio ? (
                    <>
                      <span className={styles.serieDot} aria-hidden>
                        ·
                      </span>
                      <span>{patrimonio}</span>
                    </>
                  ) : null}
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ paddingTop: 10 }}>
                <span className={styles.fieldLabel}>Abertura</span>
                <span className={styles.fieldValue}>{dash(d.abertura)}</span>
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ paddingTop: 10 }}>
                <span className={styles.fieldLabel}>Defeito</span>
                <span className={styles.fieldValue}>{dash(d.defeito)}</span>
              </td>
            </tr>
            {hasText(d.observacao) ? (
              <tr>
                <td colSpan={2} style={{ paddingTop: 10 }}>
                  <span className={styles.fieldLabel}>Observação</span>
                  <span className={styles.fieldValueMuted}>{String(d.observacao).trim()}</span>
                </td>
              </tr>
            ) : null}
            {hasText(d.observacaoGeral) ? (
              <tr>
                <td colSpan={2} style={{ paddingTop: 10 }}>
                  <span className={styles.fieldLabel}>Observação geral</span>
                  <span className={styles.fieldValueMuted}>{String(d.observacaoGeral).trim()}</span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {!layout || layout.fit ? (
        <LaudoCard c={c} obsText={c?.laudo || ''} refs={laudoRefs} />
      ) : layout.p1 != null ? (
        <LaudoCard c={c} obsText={layout.p1} hint showTail={false} />
      ) : null}
    </section>
    {layout && !layout.fit
      ? layout.pages.map((part, idx) => (
          <OsReportV2LaudoContPage
            key={`laudo-cont-${idx}`}
            d={d}
            part={part}
            idx={idx}
            total={layout.pages.length}
            fotos={part.last ? fotos.slice(0, inlineFotos) : null}
          />
        ))
      : null}
    <OsReportV2FotosPages d={d} skip={inlineFotos} />
    </>
  );
}

function LaudoCard({
  c,
  obsText,
  showCond = true,
  showObs = true,
  obsLabel = 'Observações',
  hint = false,
  showTail = true,
  refs,
}) {
  const r = refs || {};
  return (
    <div ref={r.card} className={`${styles.laudoCard} ${styles.footerBlock}`}>
      <div className={styles.laudoTitle}>Laudo Técnico</div>

      {showCond ? (
        <>
          <span ref={r.condLabel} className={styles.laudoLabel}>
            Condição do equipamento após intervenção
          </span>
          {c ? (
            <div className={`${styles.dashedBox} ${styles.boxSm} ${styles.boxFilled}`}>{c.condicao || ''}</div>
          ) : (
            <div className={`${styles.dashedBox} ${styles.boxSm}`} />
          )}
        </>
      ) : null}

      {showObs ? (
        <>
          <span ref={r.obsLabel} className={styles.laudoLabel}>
            {obsLabel}
          </span>
          {c ? (
            <div
              ref={r.obsBox}
              className={`${styles.dashedBox} ${styles.boxLg} ${styles.boxFilled} ${styles.boxFilledLg}`}
            >
              {obsText}
              {hint ? <span className={styles.contHint}>{CONT_HINT}</span> : null}
            </div>
          ) : (
            <div ref={r.obsBox} className={`${styles.dashedBox} ${styles.boxLg}`} />
          )}
        </>
      ) : null}

      {showTail ? <LaudoTail c={c} /> : null}
    </div>
  );
}

function OsReportV2LaudoContPage({ d, part, idx, total, fotos }) {
  const c = d.closure && typeof d.closure === 'object' ? d.closure : null;
  return (
    <section className={styles.page} data-os-report-page={`laudo-${idx + 1}`}>
      <div className={styles.histHeader}>
        <div className={styles.histTitleRow}>
          <span className={styles.histTitle}>Continuação do atendimento</span>
          <span className={styles.histMeta}>
            OS {dash(d.osNumber)}
            {d.equipamento ? ` · ${d.equipamento}` : ''}
            {total > 1 ? ` · ${idx + 1}/${total}` : ''}
          </span>
        </div>
        <span className={styles.histCliente}>{dash(d.cliente)}</span>
      </div>
      <LaudoCard
        c={c}
        obsText={part.text}
        showCond={part.showCond}
        showObs={part.showObs}
        obsLabel={part.showCond ? 'Observações' : 'Observações (continuação)'}
        hint={!part.last}
        showTail={part.last}
      />
      {fotos?.length ? (
        <>
          <div className={styles.sectionTitle}>Fotos do Atendimento</div>
          <FotosGrid fotos={fotos} />
        </>
      ) : null}
    </section>
  );
}

function LaudoTail({ c }) {
  return (
    <>
        <table className={styles.datesTable}>
          <tbody>
            <tr>
              <td>
                <span className={styles.laudoLabel}>Data e hora inicial</span>
                {c && (c.dataIni || c.horaIni) ? (
                  <div className={styles.dateValue}>
                    {c.dataIni || '—'}
                    <span className={styles.dateSep}>·</span>
                    {c.horaIni || '—'}
                  </div>
                ) : (
                  <div className={styles.dateFill}>___ / ___ / ______ &nbsp;&nbsp;&nbsp; ___ : ___</div>
                )}
              </td>
              <td>
                <span className={styles.laudoLabel}>Data e hora final</span>
                {c && (c.dataFim || c.horaFim) ? (
                  <div className={styles.dateValue}>
                    {c.dataFim || '—'}
                    <span className={styles.dateSep}>·</span>
                    {c.horaFim || '—'}
                  </div>
                ) : (
                  <div className={styles.dateFill}>___ / ___ / ______ &nbsp;&nbsp;&nbsp; ___ : ___</div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <table className={styles.signTable}>
          <tbody>
            <tr>
              <td>
                <div className={styles.signTitle}>Técnico Responsável</div>
                <div className={styles.signField}>
                  <div className={styles.signLabel}>Nome</div>
                  {c?.tecnico ? (
                    <div className={`${styles.line} ${styles.lineFilled}`}>{c.tecnico}</div>
                  ) : (
                    <div className={styles.line} />
                  )}
                </div>
                <div className={styles.signField}>
                  <div className={styles.signLabel}>Assinatura</div>
                  {c?.tecnico ? (
                    <div className={`${styles.line} ${styles.lineSign}`}>
                      <span className={styles.signCursive}>{toCursiveName(c.tecnico)}</span>
                    </div>
                  ) : (
                    <div className={styles.line} />
                  )}
                </div>
              </td>
              <td>
                <div className={styles.signTitle}>Solicitante / Cliente</div>
                <div className={styles.signField}>
                  <div className={styles.signLabel}>Nome</div>
                  {c?.solicitanteNome ? (
                    <div className={`${styles.line} ${styles.lineFilled}`}>{c.solicitanteNome}</div>
                  ) : (
                    <div className={styles.line} />
                  )}
                </div>
                <div className={styles.signField}>
                  <div className={styles.signLabel}>Assinatura</div>
                  {c?.assinaturaSrc ? (
                    <div className={`${styles.line} ${styles.lineSign}`}>
                      <img src={c.assinaturaSrc} alt="Assinatura do solicitante" className={styles.signImg} />
                    </div>
                  ) : (
                    <div className={styles.line} />
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
    </>
  );
}

const FOTOS_PER_PAGE = 4;

function FotosGrid({ fotos, startIndex = 0 }) {
  return (
    <div className={styles.fotosGrid}>
      {fotos.map((foto, idx) => {
        const n = startIndex + idx + 1;
        return (
          <figure key={`${foto.src}-${n}`} className={styles.fotoItem}>
            <div className={styles.fotoFrame}>
              <img src={foto.src} alt={foto.label || `Foto ${n}`} className={styles.fotoImg} />
            </div>
            <figcaption className={styles.fotoCaption}>{foto.label || `Foto ${n}`}</figcaption>
          </figure>
        );
      })}
    </div>
  );
}

function OsReportV2FotosPages({ d, skip = 0 }) {
  const fotos = listFotos(d).slice(skip);
  if (!fotos.length) return null;
  const chunks = [];
  for (let i = 0; i < fotos.length; i += FOTOS_PER_PAGE) chunks.push(fotos.slice(i, i + FOTOS_PER_PAGE));

  return chunks.map((chunk, pageIdx) => (
    <section key={`os-fotos-${pageIdx}`} className={styles.page} data-os-report-page={`fotos-${pageIdx + 1}`}>
      <div className={styles.histHeader}>
        <div className={styles.histTitleRow}>
          <span className={styles.histTitle}>Fotos do Atendimento</span>
          <span className={styles.histMeta}>
            OS {dash(d.osNumber)}
            {d.equipamento ? ` · ${d.equipamento}` : ''}
            {chunks.length > 1 ? ` · ${pageIdx + 1}/${chunks.length}` : ''}
          </span>
        </div>
        <span className={styles.histCliente}>{dash(d.cliente)}</span>
      </div>
      <FotosGrid fotos={chunk} startIndex={skip + pageIdx * FOTOS_PER_PAGE} />
    </section>
  ));
}

function OsReportV2HistoryPage({ d }) {
  const history = Array.isArray(d.history) ? d.history : [];

  return (
    <section className={styles.page} data-os-report-page="2">
      <div className={styles.histHeader}>
        <div className={styles.histTitleRow}>
          <span className={styles.histTitle}>Histórico de Atendimento</span>
          <span className={styles.histMeta}>
            OS {dash(d.osNumber)}
            {d.equipamento ? ` · ${d.equipamento}` : ''}
          </span>
        </div>
        <span className={styles.histCliente}>{dash(d.cliente)}</span>
      </div>

      {history.length === 0 ? (
        <div className={styles.emptyHist}>Nenhum histórico de atendimento encontrado.</div>
      ) : (
        <table className={styles.histTable}>
          <thead>
            <tr>
              <th style={{ width: '8%' }}>OS</th>
              <th style={{ width: '10%' }}>Data</th>
              <th style={{ width: '18%' }}>Defeito</th>
              <th style={{ width: '14%' }}>Técnico</th>
              <th style={{ width: '10%' }}>Fechamento</th>
              <th className={styles.num} style={{ width: '9%' }}>
                A4 P&B
              </th>
              <th className={styles.num} style={{ width: '9%' }}>
                A4 Cor
              </th>
              <th style={{ width: '22%' }}>Condição</th>
            </tr>
          </thead>
          <tbody>
            {history.map((row, idx) => (
              <React.Fragment key={`${row.osNum}-${idx}`}>
                <tr>
                  <td className={styles.mono}>{dash(row.osNum)}</td>
                  <td className={styles.nowrap}>{dash(row.dataOcorrencia)}</td>
                  <td className={styles.strong}>{dash(row.defeito)}</td>
                  <td>{dash(row.tecnico)}</td>
                  <td className={styles.nowrap}>{dash(row.fechamento)}</td>
                  <td className={styles.num}>{dash(row.pb)}</td>
                  <td className={styles.num}>{dash(row.cor)}</td>
                  <td>
                    <span
                      className={`${styles.condicaoPill} ${
                        row.condicaoTone === 'ok' ? styles.condicaoOk : styles.condicaoWarn
                      }`}
                    >
                      {dash(row.condicao)}
                    </span>
                  </td>
                </tr>
                <tr className={styles.histRowSep}>
                  <td colSpan={8}>
                    <div className={styles.laudoBox}>
                      <div className={styles.laudoBoxLabel}>Laudo</div>
                      <div className={styles.laudoBoxText}>{dash(row.laudo, '—')}</div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Documento A4 do report OS v2 (página 1 + opcional histórico).
 * @param {{ data?: object, pages?: object[], includeHistory?: boolean, rootRef?: React.Ref }} props
 */
export default function OsReportV2Document({ data, pages: pagesProp, includeHistory = true, rootRef }) {
  const pages =
    Array.isArray(pagesProp) && pagesProp.length
      ? pagesProp
      : [data || {}];
  const showHistory = includeHistory !== false && pages.length === 1;

  return (
    <div id="os-report-v2-print-root" ref={rootRef} className={styles.root}>
      {pages.map((pageData, idx) => (
        <React.Fragment key={`os-main-${pageData?.osNumber || idx}`}>
          <OsReportV2MainPage d={pageData || {}} />
        </React.Fragment>
      ))}
      {showHistory ? <OsReportV2HistoryPage d={pages[0] || {}} /> : null}
    </div>
  );
}
